import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FaEye } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import ReminderPreview from "./ReminderPreview";
import "./PendingView.css";

// Results > Pending: who hasn't responded yet, and reminders.
// The server decides everything (who is pending, who may be reminded, who is
// skipped and why); this view shows it and asks before sending.

const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const people = (n) => `${n} ${n === 1 ? "person" : "people"}`;

function showSkipped(title, skipped) {
    const rows = skipped.map(s => `<tr><td>${escapeHtml(s.name)}</td><td>${escapeHtml(s.reason)}</td></tr>`).join("");
    return Swal.fire({
        title,
        icon: "info",
        width: 620,
        html: `<p class="pending-skip-lead">${escapeHtml(people(skipped.length))} skipped:</p>
               <div class="pending-skip-scroll"><table class="pending-skip-table">
               <thead><tr><th>Name</th><th>Why</th></tr></thead><tbody>${rows}</tbody></table></div>`,
        confirmButtonText: "OK",
    });
}

export default function PendingView({ formId }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(() => new Set());
    const [sending, setSending] = useState(false);
    const [previewing, setPreviewing] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/reminders/${formId}/pending`, { method: "GET" });
            const body = await res.json();
            if (!res.ok) throw new Error(body.error || "Could not load who is pending.");
            setData(body);
            setError(null);
        } catch (err) {
            setError(err.message);
        }
    }, [formId]);

    useEffect(() => { if (formId) load(); }, [formId, load]);

    // While a batch is going out, refresh so the buttons unlock when it's done.
    useEffect(() => {
        if (!data || !data.in_flight) return undefined;
        const timer = setInterval(load, 15000);
        return () => clearInterval(timer);
    }, [data, load]);

    const visible = useMemo(() => {
        const list = data ? data.people : [];
        const needle = search.trim().toLowerCase();
        if (!needle) return list;
        return list.filter(p => [p.name, p.emp_id, p.department, p.location]
            .some(v => String(v || "").toLowerCase().includes(needle)));
    }, [data, search]);

    const selectable = visible.filter(p => p.has_access);
    const allVisibleSelected = selectable.length > 0 && selectable.every(p => selected.has(p.emp_id));

    const toggle = (empId) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(empId)) next.delete(empId); else next.add(empId);
        return next;
    });
    const toggleAll = () => setSelected(prev => {
        const next = new Set(prev);
        if (allVisibleSelected) selectable.forEach(p => next.delete(p.emp_id));
        else selectable.forEach(p => next.add(p.emp_id));
        return next;
    });

    const post = async (body) => {
        const res = await apiFetch(`/api/reminders/${formId}/send`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    };

    const remind = async (empIds) => {
        if (sending) return;
        setSending(true);
        try {
            const target = empIds ? { emp_ids: empIds } : {};
            const dry = await post({ ...target, dry_run: true });
            if (!dry.ok) {
                await Swal.fire("Not sent", dry.body.error || "Could not send reminders.", "warning");
                return;
            }
            if (dry.body.will_send === 0) {
                await showSkipped("Nobody to remind", dry.body.skipped);
                return;
            }
            const skippedNote = dry.body.skipped.length
                ? `<p class="pending-confirm-note">${escapeHtml(people(dry.body.skipped.length))} will be skipped; you'll see who and why after sending.</p>`
                : "";
            const answer = await Swal.fire({
                title: `Send reminders to ${people(dry.body.will_send)}?`,
                html: `<p>About ${dry.body.minutes} ${dry.body.minutes === 1 ? "minute" : "minutes"} to deliver.</p>${skippedNote}`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Send reminders",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            });
            if (!answer.isConfirmed) return;
            const sent = await post(target);
            if (!sent.ok) {
                await Swal.fire("Not sent", sent.body.error || "Could not send reminders.", "warning");
            } else if (sent.body.skipped.length) {
                await showSkipped(`Reminders sent to ${people(sent.body.sent)}`, sent.body.skipped);
            } else {
                Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Reminders sent to ${people(sent.body.sent)}`, showConfirmButton: false, timer: 2500 });
            }
            setSelected(new Set());
        } catch (err) {
            Swal.fire("Error", "Could not send reminders.", "error");
        } finally {
            setSending(false);
            load();
        }
    };

    if (error) return <p className="pending-error">{error}</p>;
    if (!data) return <p className="pending-loading">Loading who is pending...</p>;

    const busy = data.in_flight > 0;
    const locked = sending || busy || !!data.blocked;
    const selectedIds = [...selected].filter(id => data.people.some(p => p.emp_id === id && p.has_access));

    return (
        <div className="pending-view">
            {previewing && (
                <ReminderPreview
                    formId={formId}
                    onClose={() => setPreviewing(false)}
                    onSend={locked || data.pending === 0 ? undefined : () => remind(selectedIds.length ? selectedIds : null)}
                    sendLabel={selectedIds.length ? `Remind selected (${selectedIds.length})` : `Remind all pending (${data.pending})`}
                />
            )}
            <div className="pending-header">
                <h3 className="pending-count">
                    {data.pending} pending{data.without_access ? `, ${data.without_access} without access` : ""}
                </h3>
                {data.notice && <p className="pending-note">{data.notice}</p>}
                {data.blocked && <p className="pending-note pending-note-warn">Reminders can't be sent: {data.blocked}</p>}
                {busy && <p className="pending-note">The previous reminders are still being sent ({data.in_flight} left). You can send more when they're done.</p>}
            </div>

            <div className="pending-toolbar">
                <input
                    type="search"
                    className="pending-search"
                    placeholder="Search by name, id, department or location"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <div className="pending-actions">
                    <button type="button" className="pending-btn pending-btn-preview" title="Preview the reminder email" onClick={() => setPreviewing(true)}>
                        <FaEye aria-hidden="true" /> Preview email
                    </button>
                    <button type="button" className="pending-btn" disabled={locked || selectedIds.length === 0} onClick={() => remind(selectedIds)}>
                        Remind selected{selectedIds.length ? ` (${selectedIds.length})` : ""}
                    </button>
                    <button type="button" className="pending-btn pending-btn-primary" disabled={locked || data.pending === 0} onClick={() => remind(null)}>
                        Remind all pending
                    </button>
                </div>
            </div>

            {data.people.length === 0 ? (
                <p className="pending-empty">Everyone in this form's audience has responded.</p>
            ) : (
                <div className="pending-table-wrap">
                    <table className="pending-table">
                        <thead>
                            <tr>
                                <th className="pending-check">
                                    <input type="checkbox" aria-label="Select everyone shown" checked={allVisibleSelected} disabled={selectable.length === 0} onChange={toggleAll} />
                                </th>
                                <th>Name</th>
                                <th>Department</th>
                                <th>Location</th>
                                <th>Last reminded</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(p => (
                                <tr key={p.emp_id} className={p.has_access ? "" : "pending-row-noaccess"}>
                                    <td className="pending-check">
                                        <input
                                            type="checkbox"
                                            aria-label={`Select ${p.name}`}
                                            checked={selected.has(p.emp_id)}
                                            disabled={!p.has_access}
                                            onChange={() => toggle(p.emp_id)}
                                        />
                                    </td>
                                    <td>
                                        {p.name}
                                        {!p.has_access && <>{" "}<span className="pending-label">no dForms access</span></>}
                                    </td>
                                    <td>{p.department || "-"}</td>
                                    <td>{p.location || "-"}</td>
                                    <td>{p.last_reminded || "Never"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {visible.length === 0 && <p className="pending-empty">Nobody matches "{search}".</p>}
                </div>
            )}
        </div>
    );
}
